import { P2GBox, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import useLocalStorage from '~/hooks/use-local-storage';
import RequireCustomer from '../layouts/my-account/require-customer';
import { ContentContext } from '@parcel2go/npm-p2g-app';
import { RoutingConstants, RoutingConstantsTabs } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { LocalStorageKeys } from '~/apps/smartsend/lib/consts/LocalStorageKeys';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '../use-on-load';
import DesktopTitledLayout from '../layouts/my-account/desktop-layout';
import MobileLayout from '../layouts/my-account/mobile-layout';
import { GetDefaultSubItem, SubNavItemType, SubNavTabs } from '../groups/sub-nav';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

const OrderDash = lazy(() => import('./orders'));

const options: SubNavItemType[] = [
  {
    translationKey: 'TAB_ANALYTICS_ORDERS_LABEL',
    label: 'Orders',
    tab: RoutingConstantsTabs.OrdersDashboard
  }
  // Left this open for more tabs in the future
] as const;

const Wrapper = ({ isXs, children }: { isXs: boolean; children: React.ReactNode }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return isXs ? (
    <MobileLayout>{children}</MobileLayout>
  ) : (
    <DesktopTitledLayout title={t('SMARTSEND_ANALYTICS_DASH_TITLE', { defaultValue: 'Analytics' })}>
      {children}
    </DesktopTitledLayout>
  );
};

export default function Component() {
  const isXs = useP2GMediaQueryOnly('xs');
  const { track } = useSmartSendAnalytics();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { setValue: setIsSmartSendUser } = useLocalStorage(LocalStorageKeys.HasVisitedSmartSend, true);
  const [selectedOption, setSelectedOption] = useState<string>(GetDefaultSubItem(options));

  useEffect(() => {
    if (selectedOption) {
      window.location.hash = selectedOption;

      track({
        type: 'page_view',
        payload: {
          ...GenerateBaseAnalyticsPayload([]),
          page: selectedOption,
          area: RoutingConstants.Analytics
        }
      });
    }
  }, [selectedOption, track]);

  useOnLoad(() => {
    const title = t('SMARTSEND_ANALYTICS_PAGE_TITLE', { defaultValue: 'Analytics' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.SMARTSEND });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: {
        title: title,
        isHome: true,
        currentPageRoute: RoutingConstants.Analytics
      }
    });
    setIsSmartSendUser(true);
  });

  return (
    <Suspense>
      <Wrapper isXs={isXs}>
        <RequireCustomer isXs={isXs} silent={true}>
          <SubNavTabs
            options={options}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            translationNameSpace={TranslationNamespaces.MYACCOUNT}
          />
          {selectedOption === RoutingConstantsTabs.OrdersDashboard && (
            <Suspense>
              <P2GBox>
                <OrderDash isXs={isXs} />
              </P2GBox>
            </Suspense>
          )}
        </RequireCustomer>
      </Wrapper>
    </Suspense>
  );
}

Component.displayName = 'SmartSend';
